import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Switch,
  TextField,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Gavel as GavelIcon,
  Train as TrainIcon
} from '@mui/icons-material';
import { collection, getDocs, doc, updateDoc, setDoc, onSnapshot, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';

// Default court rooms data
const defaultCourtRooms = [
  { id: '10', number: '10', judgeName: '', isPresent: false, lastUpdated: null },
  { id: '22', number: '22', judgeName: '', isPresent: false, lastUpdated: null },
  { id: '44', number: '44', judgeName: '', isPresent: false, lastUpdated: null },
  { id: '48', number: '48', judgeName: '', isPresent: false, lastUpdated: null },
  { id: '63', number: '63', judgeName: '', isPresent: false, lastUpdated: null },
  { id: '65', number: '65', judgeName: '', isPresent: false, lastUpdated: null },
  { id: '66', number: '66', judgeName: '', isPresent: false, lastUpdated: null },
  { id: 'railway', number: 'Railway Mobile', judgeName: '', isPresent: false, lastUpdated: null },
];

function CourtRoomManagement() {
  const [courtRooms, setCourtRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRoom, setEditingRoom] = useState(null);
  const [newJudgeName, setNewJudgeName] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'courtRooms'), (snapshot) => {
      const rooms = [];
      snapshot.forEach((doc) => {
        rooms.push({ ...doc.data(), id: doc.id });
      });
      setCourtRooms(rooms.sort((a, b) => {
        if (a.number === 'Railway Mobile') return 1;
        if (b.number === 'Railway Mobile') return -1;
        return parseInt(a.number) - parseInt(b.number);
      }));
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handlePresenceToggle = async (roomId, currentValue) => {
    try {
      await updateDoc(doc(db, 'courtRooms', roomId), {
        isPresent: !currentValue,
        lastUpdated: new Date().toISOString()
      });
      setSnackbar({
        open: true,
        message: 'Status updated successfully',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to update status',
        severity: 'error'
      });
    }
  };

  const handleEditJudge = (room) => {
    setEditingRoom(room);
    setNewJudgeName(room.judgeName || '');
  };

  const handleSaveJudge = async () => {
    if (!editingRoom) return;

    try {
      await updateDoc(doc(db, 'courtRooms', editingRoom.id), {
        judgeName: newJudgeName
      });
      setSnackbar({
        open: true,
        message: 'Judge name updated successfully',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to update judge name',
        severity: 'error'
      });
    }
    setEditingRoom(null);
    setNewJudgeName('');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, ml: { xs: 0, md: '240px' } }}>
      <Container maxWidth="lg">
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#580100', mb: 3 }}>
          Court Room Management
        </Typography>

        <Grid container spacing={2}>
          {courtRooms.map((room) => {
            const lastUpdated = room.lastUpdated ? new Date(room.lastUpdated) : null;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const isOutdated = !lastUpdated || lastUpdated < today;

            return (
              <Grid item xs={12} sm={6} lg={4} key={room.id}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    borderLeft: '4px solid #580100'
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {room.number === 'Railway Mobile' ? (
                        <TrainIcon sx={{ fontSize: 20, color: '#580100', mr: 1 }} />
                      ) : (
                        <GavelIcon sx={{ fontSize: 20, color: '#580100', mr: 1 }} />
                      )}
                      <Typography variant="subtitle1" sx={{ color: '#580100', fontWeight: 500 }}>
                        {room.number === 'Railway Mobile' ? 'Railway Mobile' : `Court ${room.number}`}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PersonIcon sx={{ fontSize: 18, color: '#666', mr: 1 }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#333',
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {room.judgeName || 'No judge assigned'}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleEditJudge(room)}
                        sx={{ 
                          color: '#580100',
                          p: 0.5,
                          ml: 1
                        }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  <Switch
                    checked={!isOutdated && room.isPresent}
                    onChange={() => handlePresenceToggle(room.id, room.isPresent)}
                    color="success"
                    size="small"
                    sx={{ ml: 2 }}
                  />
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      <Dialog open={!!editingRoom} onClose={() => {
        setEditingRoom(null);
        setNewJudgeName('');
      }}>
        <DialogTitle>Edit Judge Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Judge Name"
            fullWidth
            value={newJudgeName}
            onChange={(e) => setNewJudgeName(e.target.value)}
            variant="outlined"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSaveJudge();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditingRoom(null);
            setNewJudgeName('');
          }}>Cancel</Button>
          <Button 
            onClick={handleSaveJudge}
            sx={{ color: '#580100' }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CourtRoomManagement;
